import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { EnvelopeSimple, Phone, Star } from "@phosphor-icons/react";
import { Header, MobileNav } from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { formatDateShort } from "@/lib/constants";
import { toast } from "@/components/ui/sonner";
import { useBuilding } from "@/context/BuildingContext";

const SERVICES_IMG =
  "https://images.unsplash.com/photo-1768321916212-17ae334a3d63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxwbHVtYmVyJTIwcmVwYWlyJTIwaG9tZXxlbnwwfHx8fDE3OTE3MjQ2NzN8MA&ixlib=rb-4.1.0&q=85";

export default function Services() {
  const BUILDING = useBuilding();
  const ratingsEnabled = BUILDING.features?.ratings !== false;
  const [services, setServices] = useState(null);
  const [voting, setVoting] = useState(null);
  const [myVotes, setMyVotes] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentSent, setCommentSent] = useState({});

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, []);

  const rate = async (s, n) => {
    if (voting) return;
    setVoting(s.id);
    try {
      const { data } = await api.post(`/services/${s.id}/rate`, { rating: n });
      setServices((list) =>
        list.map((x) => (x.id === s.id ? { ...x, rating_avg: data.rating_avg, rating_count: data.rating_count } : x))
      );
      setMyVotes((v) => ({ ...v, [s.id]: n }));
      toast.success("Merci pour votre note !");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setVoting(null);
  };

  const sendComment = async (s) => {
    const text = (commentDrafts[s.id] || "").trim();
    if (text.length < 3) return;
    try {
      await api.post(`/services/${s.id}/rate`, { rating: myVotes[s.id], comment: text });
      setCommentSent((c) => ({ ...c, [s.id]: true }));
      toast.success("Merci ! Votre commentaire sera visible après validation par le gestionnaire.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bestByCategory = {};
  (services || []).forEach((s) => {
    if ((s.rating_count || 0) > 0 && !bestByCategory[s.category]) bestByCategory[s.category] = s.id;
  });

  return (
    <div className="min-h-screen pb-24 md:pb-0" data-testid="services-page">
      <Header />
      <main className="mx-auto max-w-3xl px-5 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{BUILDING.name}</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-[-0.02em] sm:text-5xl">Services aux résidents</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Des professionnels de confiance pour votre logement, sélectionnés par le gestionnaire de la résidence.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-8 overflow-hidden rounded-3xl border border-black/5 shadow-[0_14px_40px_rgb(0,0,0,0.08)]"
        >
          <img src={SERVICES_IMG} alt="Artisan intervenant dans un logement" className="aspect-[21/9] w-full object-cover" />
        </motion.div>

        <div className="mt-8 space-y-4">
          {services === null && (
            <div className="space-y-4" data-testid="services-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-3xl bg-black/5" />
              ))}
            </div>
          )}
          {services?.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: Math.min(i * 0.06, 0.4) }}
              className="rounded-3xl border border-black/5 bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              data-testid={`service-card-${i}`}
            >
              <div>
                {ratingsEnabled && (
                <>
                <div className="mb-1.5 flex items-center gap-0.5" data-testid={`service-stars-${i}`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => rate(s, n)}
                      disabled={voting === s.id}
                      aria-label={`Noter ${s.name} ${n} sur 5`}
                      data-testid={`service-rate-${i}-star-${n}`}
                      className="rounded p-0.5 transition-transform duration-150 hover:scale-125 disabled:opacity-50"
                    >
                      <Star
                        size={17}
                        weight={n <= Math.round(s.rating_avg || 0) ? "fill" : "regular"}
                        className={n <= Math.round(s.rating_avg || 0) ? "text-[#E8A33D]" : "text-black/20"}
                      />
                    </button>
                  ))}
                  <span className="ml-1 text-[11px] font-semibold text-muted-foreground" data-testid={`service-rating-label-${i}`}>
                    {(s.rating_count || 0) > 0
                      ? `${String(s.rating_avg).replace(".", ",")}/5 · ${s.rating_count} avis`
                      : "Noter ce prestataire"}
                  </span>
                  {bestByCategory[s.category] === s.id && (
                    <span
                      data-testid={`service-best-${i}`}
                      className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-[#E8A33D]/15 px-2.5 py-0.5 text-[11px] font-bold text-[#9C6A1B]"
                    >
                      <Star size={10} weight="fill" /> Mieux noté
                    </span>
                  )}
                </div>
                {myVotes[s.id] && !commentSent[s.id] && (
                  <div className="mb-1.5 flex gap-2" data-testid={`service-comment-box-${i}`}>
                    <input
                      type="text"
                      value={commentDrafts[s.id] || ""}
                      onChange={(e) => setCommentDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                      placeholder="Ajouter un petit commentaire (optionnel)…"
                      maxLength={500}
                      data-testid={`service-comment-input-${i}`}
                      className="w-full rounded-full border border-black/10 px-4 py-2 text-xs outline-none transition-colors focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={() => sendComment(s)}
                      disabled={(commentDrafts[s.id] || "").trim().length < 3}
                      data-testid={`service-comment-send-${i}`}
                      className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-40"
                    >
                      Envoyer
                    </button>
                  </div>
                )}
                {commentSent[s.id] && (
                  <p className="mb-1.5 text-[11px] font-semibold text-[#007A4D]" data-testid={`service-comment-pending-${i}`}>
                    Commentaire envoyé — visible après validation par le gestionnaire.
                  </p>
                )}
                </>
                )}
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display text-base font-bold tracking-tight">{s.name}</p>
                      <span className="rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-bold text-secondary-foreground">{s.category}</span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{s.description}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <a
                  href={`tel:${s.phone.replace(/\s/g, "")}`}
                  data-testid={`service-phone-${i}`}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-xs font-bold text-primary-foreground transition-colors duration-200 hover:bg-[#265442]"
                >
                  <Phone size={14} weight="fill" />
                  {s.phone}
                </a>
                {s.has_email && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/services/${s.id}/email`}
                    data-testid={`service-email-${i}`}
                    className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2.5 text-xs font-bold transition-colors hover:border-primary hover:text-primary"
                  >
                    <EnvelopeSimple size={14} weight="bold" />
                    E-mail
                  </a>
                )}
              </div>
              {(s.reviews || []).length > 0 && ratingsEnabled && (
                <div className="mt-3 space-y-2 border-t border-black/5 pt-3" data-testid={`service-reviews-${i}`}>
                  {s.reviews.map((rev, j) => (
                    <div key={j} data-testid={`service-review-${i}-${j}`}>
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 flex shrink-0 gap-px">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} size={11} weight={n <= rev.rating ? "fill" : "regular"} className={n <= rev.rating ? "text-[#E8A33D]" : "text-black/20"} />
                          ))}
                        </span>
                        <p className="text-xs leading-relaxed text-foreground/70">
                          « {rev.comment} » <span className="text-muted-foreground">· {formatDateShort(rev.at)}</span>
                        </p>
                      </div>
                      {rev.response && (
                        <div className="ml-6 mt-1.5 rounded-xl bg-primary/5 px-3 py-2" data-testid={`review-response-${i}-${j}`}>
                          <p className="text-[10px] font-bold uppercase tracking-wide text-primary">Réponse du gestionnaire</p>
                          <p className="mt-0.5 text-xs leading-relaxed text-foreground/70">{rev.response}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </main>
      <MobileNav />
    </div>
  );
}
